/**
 * useRelationalFields Hook
 *
 * Given a collection and its base fields, returns an extended fields array
 * that includes sub-fields of related collections reachable via:
 *   - M2O relations  (direct FK: "category_id.name")
 *   - M2M relations  (through junction table: "categories.category_id.name")
 *
 * These dot-notation field objects are compatible with FilterNodeInput's
 * existing `fields.find(f => f.field === fieldPath)` lookup.
 */

import { useState, useEffect } from 'react';
import { api, type ApiListResponse } from '@/lib/api';
import { getFieldDisplayName } from '@/utils/get-field-display-name';
import type { Field } from './useFields';

export interface Relation {
	collection: string;
	field: string;
	related_collection: string | null;
	schema: unknown | null;
	meta: {
		one_collection?: string | null;
		one_field?: string | null;
		junction_field?: string | null;
		sort_field?: string | null;
		[key: string]: unknown;
	} | null;
}

/**
 * Builds a human-readable display name for a field or relation.
 * e.g. "category_id" → "Category"  |  "categories" → "Categories"
 */
function formatRelationLabel(field: string): string {
	return field
		.replace(/_id$/, '')
		.replace(/_/g, ' ')
		.replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Extended field marker so the field-picker can render group separators.
 */
export interface ExtendedField extends Field {
	_isRelated?: boolean;
	/** The top-level field name on the base collection (FK for M2O, alias for M2M) */
	_relationField?: string;
	/** Human-readable label for the relation group */
	_relationLabel?: string;
	/** 'M2O' | 'M2M' — kind of relation this field came through */
	_relationType?: 'M2O' | 'M2M';
}

export function useRelationalFields(collection: string | null, baseFields: Field[]): ExtendedField[] {
	const [relatedFields, setRelatedFields] = useState<ExtendedField[]>([]);

	useEffect(() => {
		if (!collection || baseFields.length === 0) {
			setRelatedFields([]);
			return;
		}

		let cancelled = false;

		async function fetchRelated() {
			try {
				// 1. Fetch ALL relations (we need both M2O and M2M sides)
				const relRes = await api.get<ApiListResponse<Relation>>('/relations');
				const allRelations: Relation[] = relRes.data || [];

				// ── M2O: relations where this collection is the owning side ──────────
				const m2oRelations = allRelations.filter(
					(r) =>
						r.collection === collection &&
						r.related_collection &&
						// Exclude junction-side records (they also have collection === collection
						// but expose meta.one_collection on the OTHER collection)
						!r.meta?.junction_field
				);

				// ── M2M: junction relations that "belong" to this collection ─────────
				// Identified by meta.one_collection === collection && meta.junction_field present
				const m2mRelations = allRelations.filter(
					(r) => r.meta?.one_collection === collection && r.meta?.junction_field && r.meta?.one_field // the alias field on the base collection
				);

				if ((m2oRelations.length === 0 && m2mRelations.length === 0) || cancelled) {
					setRelatedFields([]);
					return;
				}

				// 2a. Resolve M2O sub-fields
				const m2oFetched = await Promise.all(
					m2oRelations.map(async (rel) => {
						try {
							const fRes = await api.get<ApiListResponse<Field>>(`/fields/${rel.related_collection}`);
							const relFields: Field[] = fRes.data || [];
							const relLabel = formatRelationLabel(rel.field);

							return relFields
								.filter((f) => !f.meta?.hidden)
								.map(
									(f): ExtendedField => ({
										...f,
										field: `${rel.field}.${f.field}`,
										name: `${relLabel} > ${getFieldDisplayName(f)}`,
										collection: collection!,
										_isRelated: true,
										_relationField: rel.field,
										_relationLabel: relLabel,
										_relationType: 'M2O',
									})
								);
						} catch {
							return [];
						}
					})
				);

				// 2b. Resolve M2M sub-fields through junction → related collection
				const m2mFetched = await Promise.all(
					m2mRelations.map(async (rel) => {
						try {
							// rel.collection      = junction collection
							// rel.field           = junction FK pointing to base collection
							// rel.meta.junction_field = junction FK pointing to related collection
							// rel.meta.one_field  = alias field on the base collection (e.g. "categories")
							const junctionField = rel.meta!.junction_field!; // e.g. "category_id"
							const aliasField = rel.meta!.one_field!; // e.g. "categories"

							// Find the OTHER side of the junction to get the related collection
							const otherSide = allRelations.find(
								(r) => r.collection === rel.collection && r.field === junctionField && r.related_collection
							);
							if (!otherSide?.related_collection) return [];

							const relatedCollection = otherSide.related_collection;

							// Fetch fields of the related collection
							const fRes = await api.get<ApiListResponse<Field>>(`/fields/${relatedCollection}`);
							const relFields: Field[] = fRes.data || [];
							const relLabel = formatRelationLabel(aliasField);

							// Build path: aliasField.junctionField.sub_field
							// e.g. "categories.category_id.name"
							return relFields
								.filter((f) => !f.meta?.hidden)
								.map(
									(f): ExtendedField => ({
										...f,
										field: `${aliasField}.${junctionField}.${f.field}`,
										name: `${relLabel} > ${getFieldDisplayName(f)}`,
										collection: collection!,
										_isRelated: true,
										_relationField: aliasField,
										_relationLabel: `${relLabel} (M2M)`,
										_relationType: 'M2M',
									})
								);
						} catch {
							return [];
						}
					})
				);

				if (!cancelled) {
					setRelatedFields([...m2oFetched.flat(), ...m2mFetched.flat()]);
				}
			} catch {
				if (!cancelled) setRelatedFields([]);
			}
		}

		fetchRelated();

		return () => {
			cancelled = true;
		};
	}, [collection, baseFields]);

	// Combine base fields (cast to ExtendedField) + related fields
	return [...(baseFields as ExtendedField[]), ...relatedFields];
}
